import { AiTag, EarlyAccessTag, NewTag } from '../tag';
import { Icon } from '../icon';
import AiStudioMenuIcon from 'assets/images/others/ai-studio.svg';
import DamMenuIcon from 'assets/images/others/dam.svg';
import ConfiguratorMenuIcon from 'assets/images/others/product-configurator.svg';
import CustomizerMenuIcon from 'assets/images/others/visual-customizer.svg';
import React, { FunctionComponent, useRef } from 'react';
import { useAppState } from 'src/shared/appState';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { T } from 'src/shared/helpers';
import { EnvType } from 'src/shared/hooks';
import AngleDown from 'src/shared/svg/angleDown';
import {
    ArrowToolContainer,
    ContainerText,
    IconMenuContainer,
    MenuItem,
    MenuItemIcon,
    MenuItemStrong,
    MenuLeft,
    NoWrap,
    PopupMenuItemLarge,
    PopupMenuItemLargeIcon,
    SeparatorMenu,
    ToolDropdownPortal
} from './globalsToolbar';
import SocialMenu from './socialMenu';
import { getTools } from './toolbar';
interface LeftMenuProps {
    isMenuHidden: boolean;
    forceTools: boolean;
    userInfo: any;
    ecommerceSettings: any;
    closeAll: () => void;
    setIsToolPopupVisible: (visible: boolean) => void;
    setIsSocialMenuVisible: (visible: boolean) => void;
    isSocialMenuVisible: boolean;
    isToolPopupVisible: boolean;
    handlerOnClick: (link: string, type: EnvType) => void;
    goToBlog: () => void;
    setLastUsedTool: (type: EnvType) => void;
    userID?: number;
    toolsArray?: ReturnType<typeof getTools>;
}

const LeftMenu: FunctionComponent<LeftMenuProps> = ({
    isMenuHidden,
    forceTools,
    userInfo,
    ecommerceSettings,
    closeAll,
    setIsToolPopupVisible,
    setIsSocialMenuVisible,
    isSocialMenuVisible,
    isToolPopupVisible,
    handlerOnClick,
    goToBlog,
    userID,
    toolsArray
}) => {
    const { state } = useAppState();
    const toolMenuRef = useRef<HTMLDivElement>(null);

    const isNewConfiguratorBackoffice = useFeatureFlagWithConditions('newConfiguratorBackoffice', {
        userId: (userID || 0).toString()
    });

    // Use the passed toolsArray or fallback to default tools
    const currentTools = toolsArray || getTools(isNewConfiguratorBackoffice);

    // Helper functions to determine which tool to display
    const getActiveToolType = () => {
        if (isNewConfiguratorBackoffice) {
            // When FF is active, use lastUsedTool (1=CUSTOMIZER, 2=CONFIGURATOR, 3=VIEWER)
            switch (state.lastUsedTool) {
                case 1:
                    return 'CUSTOMIZER';
                case 2:
                    return 'CONFIGURATOR';
                case 3:
                    return 'VIEWER';
                default:
                    return 'CUSTOMIZER';
            }
        } else {
            // When FF is disabled, use URL-based logic
            const url = window.location.href;

            // CONFIGURATOR: landings/configurator OR /Composer
            if (url.indexOf('landings/configurator') !== -1 || url.indexOf('/Composer') !== -1) {
                return 'CONFIGURATOR';
            }

            // VIEWER: landings/viewer OR Admin/DAM
            if (url.indexOf('landings/viewer') !== -1 || url.indexOf('Admin/DAM') !== -1) {
                return 'VIEWER';
            }

            // CUSTOMIZER: all other cases
            return 'CUSTOMIZER';
        }
    };

    const activeToolType = getActiveToolType();

    const renderToolIcon = (toolType: string) => {
        const tool = currentTools.find((t) => t.type === toolType);

        switch (toolType) {
            case 'CONFIGURATOR':
                return (
                    <>
                        <IconMenuContainer>
                            <Icon>
                                <ConfiguratorMenuIcon />
                            </Icon>
                        </IconMenuContainer>
                        <ContainerText data-testid='toolbar-active-tool-configurator' data-track='tool'>
                            {tool?.name}
                        </ContainerText>
                    </>
                );
            case 'VIEWER':
                return (
                    <>
                        <IconMenuContainer>
                            <Icon>
                                <DamMenuIcon />
                            </Icon>
                        </IconMenuContainer>
                        <ContainerText data-testid='toolbar-active-tool-viewer' data-track='tool'>
                            {tool?.name}
                        </ContainerText>
                    </>
                );
            case 'AI_STUDIO':
                return (
                    <>
                        <IconMenuContainer>
                            <Icon>
                                <AiStudioMenuIcon />
                            </Icon>
                        </IconMenuContainer>
                        <ContainerText data-testid='toolbar-active-tool-ai-agents' data-track='tool'>
                            {tool?.name}
                        </ContainerText>
                    </>
                );
            case 'CUSTOMIZER':
            default:
                return (
                    <>
                        <IconMenuContainer>
                            <Icon>
                                <CustomizerMenuIcon />
                            </Icon>
                        </IconMenuContainer>
                        <ContainerText data-testid='toolbar-active-tool-customizer' data-track='tool'>
                            {tool?.name}
                        </ContainerText>
                    </>
                );
        }
    };
    return (
        <MenuLeft>
            {!isMenuHidden && !forceTools && userInfo?.userID && userInfo.name && (
                <>
                    <MenuItem ref={toolMenuRef} onClick={(e) => e.nativeEvent.stopImmediatePropagation()}>
                        <NoWrap
                            data-testid='toolbar-active-tool'
                            $padding='0 5px'
                            onClick={() => {
                                closeAll();
                                setIsToolPopupVisible(true);
                            }}
                        >
                            {ecommerceSettings?.ecommerceName.toUpperCase() != 'WIX' && renderToolIcon(activeToolType)}
                            {ecommerceSettings?.ecommerceName.toUpperCase() == 'WIX' && activeToolType !== 'CONFIGURATOR' && renderToolIcon(activeToolType)}
                        </NoWrap>{' '}
                        <MenuItemIcon
                            onClick={() => {
                                closeAll();
                                setIsToolPopupVisible(true);
                            }}
                        >
                            <ArrowToolContainer>
                                <AngleDown />
                            </ArrowToolContainer>
                        </MenuItemIcon>
                        <ToolDropdownPortal
                            isVisible={isToolPopupVisible}
                            triggerRef={toolMenuRef}
                            $width={400}
                            $align={'left'}
                            onClose={() => setIsToolPopupVisible(false)}
                        >
                            {currentTools.map((tool) => {
                                if (ecommerceSettings?.ecommerceName.toUpperCase() == 'WIX' && tool.type === 'CONFIGURATOR') {
                                    return;
                                }

                                return (
                                    <PopupMenuItemLarge
                                        key={tool.type}
                                        onClick={(e) => {
                                            console.log('🔥 Tool clicked:', tool.type, tool.url);
                                            handlerOnClick(tool.url, tool.type as EnvType);
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // Prima esegui l'azione
                                            // Poi chiudi il dropdown con un piccolo delay
                                            setTimeout(() => {
                                                closeAll();
                                            }, 100);
                                        }}
                                        data-testid={`tool-option-${tool.type.toLowerCase()}`}
                                    >
                                        <PopupMenuItemLargeIcon>
                                            <Icon>{tool.icon}</Icon>
                                        </PopupMenuItemLargeIcon>

                                        <strong>
                                            <span>{tool.name}</span>
                                            {tool.isAI && <AiTag>AI</AiTag>}
                                            {tool.isNew && <NewTag>NEW</NewTag>}
                                            {tool.isEarlyAccess && <EarlyAccessTag>EARLY ACCESS</EarlyAccessTag>}
                                        </strong>
                                        <span>{tool.description}</span>
                                    </PopupMenuItemLarge>
                                );
                            })}
                        </ToolDropdownPortal>
                    </MenuItem>

                    {!isMenuHidden && <SeparatorMenu />}
                </>
            )}

            {!isMenuHidden && userInfo?.userID && userInfo.userID > 0 && (
                <>
                    <MenuItem onClick={(e) => e.nativeEvent.stopImmediatePropagation()}>
                        <MenuItemStrong
                            onClick={() => {
                                closeAll();
                                setIsSocialMenuVisible(!isSocialMenuVisible);
                            }}
                        >
                            {T._('Follow us', 'Admin')}
                        </MenuItemStrong>
                        <MenuItemIcon
                            onClick={() => {
                                closeAll();
                                setIsSocialMenuVisible(!isSocialMenuVisible);
                            }}
                            $isDark={true}
                        >
                            <AngleDown />
                        </MenuItemIcon>
                        <SocialMenu isSocialPopupVisible={isSocialMenuVisible} />
                    </MenuItem>

                    <SeparatorMenu />

                    <MenuItemStrong onClick={() => goToBlog()}>{T._('Blog', 'Admin')}</MenuItemStrong>
                </>
            )}
        </MenuLeft>
    );
};

export default LeftMenu;
